import React, { useEffect, useState } from 'react';
import Input from '../../components/input/Input';
import DatePicker from "react-datepicker";
import { toast } from 'react-toastify';
import Checkbox from '@mui/material/Checkbox';
import { numberFields, validateNumberInput } from '../../utils/formValidations';
import useManageOrder from '../order/useManageOrder';
import useShipDate from '../setting/shipDate/useShipDate';
import useAirItemTypeList from '../../hooks/useAirItemTypeList';
import Modal from 'react-modal';
import OrderChangeLog from '../order/OrderChangeLog';
import { useAuth } from '../../context/AuthContext';
import { formatDate } from '../../utils/dateUtils';
import handCarryService from '../handCarry/handCarryService';
import AirItemTypeService from '../setting/airItemType/AirItemTypeService';
import AirItemTypeTripleDropdown from '../../sharedComponents/AirItemTypeTripleDropdown';
import OrderByOptionService from '../setting/orderByOption/OrderByOptionService';
import OrderService from '../order/OrderService';
import { REASONS } from '../money/refundTransfer/refundShared';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/solid';
import { WeightAssistTag, useWeightAssists } from '../caseTicket/WeightAssistTag';

const customStylesOrderDetail = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)', // This sets the overlay color
    zIndex: 1000
  },
  content: {
    top: '50%',
    left: '55%',
    right: 'auto',
    bottom: 'auto',
    height: '800px',
    width: '1300px',
    marginRight: '0%',
    transform: 'translate(-50%, -50%)',
  },
};

const OrderDetail = ({ order: initialOrder, handleSuccessUpdate}) => {

		const { authState } = useAuth();
    const [order, setOrder] = useState(initialOrder);
    const [originalOrder, setOriginalOrder] = useState(initialOrder); // Store original data for revert
    const [isEditMode, setIsEditMode] = useState(false);
		const [logIsOpen, setLogIsOpen] = useState(false)
		const { shipDates, getShipDates } = useShipDate();
		const { airItemTypeList, isLoading: isLoadingAirItemTypes } = useAirItemTypeList();
		
		// Air item type editing state
		const [airItemTypes, setAirItemTypes] = useState([]); // Full list for dropdown
		const [editingAirTypes, setEditingAirTypes] = useState(false);
		const [airTypeRows, setAirTypeRows] = useState([]); // [{ airItemTypeId, qty }]
		const [savingAirTypes, setSavingAirTypes] = useState(false);
		const [orderByOptions, setOrderByOptions] = useState([]);

		// Extra JP fee lines (e.g. mercari anshin) — suggestions come from the backend resolver
		const [feeSuggestions, setFeeSuggestions] = useState([]);
		const [feeBusy, setFeeBusy] = useState(false);

		useEffect(() => {
			if (!order?.url) return;
			OrderService.suggestExtraFees(order.url)
				.then(res => setFeeSuggestions(res.data?.suggestions || []))
				.catch(() => setFeeSuggestions([]));
		}, [order?.url]);

		const handleAddExtraFee = async (suggestion) => {
			setFeeBusy(true);
			try {
				const res = await OrderService.addExtraFee({
					orderId: order.id,
					type: suggestion.type,
					label: suggestion.label,
					amountJpy: suggestion.amountJpy,
				});
				setOrder(prev => ({ ...prev, extraJpFees: res.data.extraJpFees, extraJpFeesPaid: res.data.extraJpFeesPaid }));
				toast.success(`เพิ่ม${suggestion.label} ¥${suggestion.amountJpy} แล้ว`);
				handleSuccessUpdate?.();
			} catch (err) {
				toast.error(err?.response?.data?.message || 'เพิ่มค่าบริการไม่สำเร็จ');
			} finally {
				setFeeBusy(false);
			}
		};

		const handleRemoveExtraFee = async (fee) => {
			if (!window.confirm(`ลบ ${fee.label || fee.type} ฿${fee.amountThb} ออกจากออเดอร์นี้ ?`)) return;
			setFeeBusy(true);
			try {
				const res = await OrderService.removeExtraFee({ orderId: order.id, type: fee.type });
				setOrder(prev => ({ ...prev, extraJpFees: res.data.extraJpFees }));
				toast.success('ลบค่าบริการแล้ว');
				handleSuccessUpdate?.();
			} catch (err) {
				toast.error(err?.response?.data?.message || 'ลบค่าบริการไม่สำเร็จ');
			} finally {
				setFeeBusy(false);
			}
		};

		console.log(initialOrder)

		const [weightAssistVersion, setWeightAssistVersion] = useState(0);
		const weightAssists = useWeightAssists([order?.id], weightAssistVersion);

		useEffect(() => {
			getShipDates();
			console.log(order)
		}, []);

		useEffect(() => {
			OrderByOptionService.getActiveOrderByOptions()
				.then(res => setOrderByOptions(res.data || []))
				.catch(err => console.error('Failed to fetch order-by options:', err));
		}, []);
		
		// Load air item types for dropdown when editing
		useEffect(() => {
			if (editingAirTypes && airItemTypes.length === 0) {
				AirItemTypeService.getAirItemTypes({ isActive: true })
					.then(res => setAirItemTypes(res.data?.data || []))
					.catch(err => console.error('Failed to load air item types', err));
			}
		}, [editingAirTypes]);
		
		// Initialize airTypeRows when starting edit
		const startEditingAirTypes = () => {
			const existing = order.handCarry?.airItems || [];
			if (existing.length > 0) {
				setAirTypeRows(existing.map(ai => ({ airItemTypeId: ai.airItemType?.id || ai.airItemTypeId, qty: ai.qty || 1 })));
			} else {
				setAirTypeRows([{ airItemTypeId: '', qty: 1 }]);
			}
			setEditingAirTypes(true);
		};
		
		const cancelEditingAirTypes = () => {
			setEditingAirTypes(false);
			setAirTypeRows([]);
		};
		
		const addAirTypeRow = () => {
			setAirTypeRows(prev => [...prev, { airItemTypeId: '', qty: 1 }]);
		};
		
		const removeAirTypeRow = (index) => {
			setAirTypeRows(prev => prev.filter((_, i) => i !== index));
		};
		
		const updateAirTypeRow = (index, field, value) => {
			setAirTypeRows(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
		};
		
		const saveAirTypes = async () => {
			const validItems = airTypeRows
				.filter(row => row.airItemTypeId)
				.map(row => {
					const type = airItemTypes.find(t => t.id === row.airItemTypeId);
					const qty = type?.calculationMethod === 'per_item' ? (row.qty || 1) : 1;
					return { airItemTypeId: row.airItemTypeId, qty };
				});
			
			if (validItems.length === 0) {
				toast.warning('กรุณาเลือกอย่างน้อย 1 ประเภท');
				return;
			}
			
			const handCarryId = order.handCarry?.id ?? order.handCarry?.airItems?.[0]?.handCarryListId;
			const orderId = order.id;
			if (!handCarryId && !orderId) {
				toast.error('ไม่พบข้อมูล Hand Carry หรือ Order');
				return;
			}
			
			setSavingAirTypes(true);
			try {
				if (handCarryId) {
					await handCarryService.assignTypesToHandCarry(handCarryId, validItems);
				} else {
					await handCarryService.assignTypesToHandCarryByOrderId(orderId, validItems);
				}
				toast.success('บันทึกประเภทสินค้าสำเร็จ');
				// Update local order state with new air items
				const newAirItems = validItems.map((vi, idx) => ({
					id: idx,
					airItemTypeId: vi.airItemTypeId,
					qty: vi.qty,
					airItemType: airItemTypes.find(t => t.id === vi.airItemTypeId)
				}));
				setOrder(prev => ({
					...prev,
					handCarry: { ...prev.handCarry, airItems: newAirItems }
				}));
				setEditingAirTypes(false);
				setAirTypeRows([]);
				// Refresh parent orders so close/reopen shows updated data
				if (handleSuccessUpdate) handleSuccessUpdate();
			} catch (err) {
				toast.error('ไม่สามารถบันทึกได้');
				console.error(err);
			} finally {
				setSavingAirTypes(false);
			}
		};

		const {
			updateOrder
		} = useManageOrder()

		useEffect(() => {
			let updatedOrder = { ...initialOrder };

			if (initialOrder.ShipDate) {
					updatedOrder.shipDate = initialOrder.ShipDate.shipDate;
			}

			setOrder(updatedOrder);
			setOriginalOrder(updatedOrder); // Also store the processed original data
	}, [initialOrder]);

		// Payment/status fields the payment webhook owns. Block a save only when one of
		// THESE changed server-side since the form loaded — not on any updatedAt bump,
		// which fires constantly from benign background writes and would block every edit.
		const PROTECTED_FIELDS = [
			'productPayment', 'transactionFeePaid', 'shipJpPaid',
			'weightPaid', 'shipThPaid', 'extraJpFeesPaid', 'orderStatus',
		];
		const protectedFieldChanged = (baseline, fresh) =>
			PROTECTED_FIELDS.some(f => String(fresh?.[f] ?? '') !== String(baseline?.[f] ?? ''));

		// Re-read the order and check whether a protected field changed since load.
		// Returns true when it is still safe to save.
		const isStillFresh = async () => {
			const baseline = originalOrder;
			if (!baseline?.uuid || !baseline?.shop) return true; // no way to check → don't block
			try {
				const res = await OrderService.getOrderByUuid(baseline.uuid, baseline.shop);
				return !protectedFieldChanged(baseline, res.data);
			} catch (err) {
				// Fail open. getOrderByUuid returns the row for ANY existing order (no status
				// filter), so a real change never 404s — a 404/error means the endpoint is
				// unreachable or params are bad, which must never block a legitimate save.
				return true;
			}
		};

		const ensureFresh = async () => {
			if (await isStillFresh()) return true;
			toast.warning('ออเดอร์นี้เพิ่งถูกอัปเดต กรุณาโหลดใหม่ก่อนบันทึก');
			return false;
		};

		const formatDateToYYYYMMDD = (date) => {
			if (!date) return null;
			const d = new Date(date);
			let month = '' + (d.getMonth() + 1);
			let day = '' + d.getDate();
			const year = d.getFullYear();
		
			if (month.length < 2) month = '0' + month;
			if (day.length < 2) day = '0' + day;
		
			return [year, month, day].join('-');
		};

		const toggleEditMode = async () => {
			if(isEditMode){
				// Bail (staying in edit mode) if a protected field changed under us.
				if (!(await ensureFresh())) return;

				// Send only the fields the admin changed (+ identifiers) so we never
				// overwrite fields they didn't touch — e.g. payment status set by a webhook.
				const changed = {};
				Object.keys(order).forEach(k => {
					if (order[k] !== originalOrder?.[k]) changed[k] = order[k];
				});
				const payload = { uuid: order.uuid, id: order.id, ...changed };

				const result = await updateOrder(payload, authState.user.id)
				if(result === true){
					toast.success("Update Successful")
					// The server may store a different weight fee than typed (a ตามของ
					// discount comes off on save) — show what was stored, not what was typed.
					let saved = { ...order };
					if ('weight' in changed || 'weightPrice' in changed) {
						try {
							const fresh = (await OrderService.getOrderByUuid(order.uuid, order.shop)).data;
							if (fresh) saved = { ...saved, weight: fresh.weight, weightPrice: fresh.weightPrice, weightPaid: fresh.weightPaid };
						} catch (err) { /* keep what was typed */ }
						setOrder(saved);
						setWeightAssistVersion((v) => v + 1);
					}
					// Update original order data on success
					setOriginalOrder(saved);
					// Don't call handleSuccessUpdate - keep the profile in place
					setTimeout(() => {
						handleSuccessUpdate();
					}, 1000); // Adjust the delay time as needed (1000 ms = 1 second)
				}else{
					toast.error(result)
					// Revert to original data on failure
					setOrder({ ...originalOrder });
				}
			}
			setIsEditMode(!isEditMode);
		};

		const handleDateChange = (name, selectedDate) => {
			if (name === 'handCarryDate') {
				// Special handling for handCarryDate (nested structure)
				const formattedDate = selectedDate ? formatDateToYYYYMMDD(selectedDate) : null;
				setOrder(prevOrder => ({
					...prevOrder,
					handCarry: prevOrder.handCarry ? 
						{ ...prevOrder.handCarry, handCarryDate: formattedDate } : 
						{ handCarryDate: formattedDate }
				}));
			} else {
				// Handle other date fields as before
				setOrder(prevOrder => ({
					...prevOrder,
					[name]: formatDateToYYYYMMDD(selectedDate)
				}));
			}
		};

		const handleOpenLog = () => {
			setLogIsOpen(true)
		}

		const handleCloseLog = () => {
			setLogIsOpen(false)
		}

		const handleInputChange = (name, value) => {
			if (numberFields.has(name)) {
				value = validateNumberInput(value, order[name]);
			}
			
			// Special handling for handCarry
			if (name === 'isHandCarry') {
				const updatedValue = value === "" ? null : value;
				setOrder(prevOrder => ({
					...prevOrder,
					handCarry: updatedValue ? 
						{ handCarryDate: prevOrder.handCarry?.handCarryDate || null } : 
						null
				}));
				return;
			}

		
			
			const updatedValue = value === "" ? null : value;
			setOrder(prevOrder => ({
				...prevOrder,
				[name]: updatedValue
			}));
		}
		
		const handleSelectChange = (name, value) => {
			const updatedValue = value === "" ? null : value;
			setOrder(prevOrder => ({
				...prevOrder,
				[name]: updatedValue
		}));
		console.log(order)
		}
		
		const renderThaiGroup = () => {
			return <>
				{/* ShipThPaid */}
				<div className='col-span-2'>
					<label htmlFor="shipThPaid" className="pl-1 flex text-sm font-medium leading-6 text-gray-900 items-center">
						ชำระค่าส่งไทย
					</label>
					<select 
						id="shipThPaid"
						className={`text-red-900 text-sm w-full py-2 px-1 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 ${!isEditMode ? 'bg-gray-200 cursor-not-allowed' : ''}`}
						value={order.shipThPaid ? "true" : "false"} 
						onChange={(e) => handleSelectChange("shipThPaid", e.target.value === 'true')}
						disabled={!isEditMode}
					>
						<option value="false">รอชำระ</option>
						<option value="true">ชำระครบแล้ว</option>
					</select>
				</div>

				<Input 
					columnSpan={2} 
					title='ค่าส่งในไทย' 
					value={order.deliveryFeeTh}
					disable={!isEditMode}
					onChange={(value) => handleInputChange('deliveryFeeTh', value)}
				/>
				<Input 
					columnSpan={2} 
					title='เลขพัสดุไทย' 
					value={order.trackingTh}
					disable={!isEditMode}
					onChange={(value) => handleInputChange('trackingTh', value)}
				/>
				<Input 
					columnSpan={2} 
					title='ขนส่งในไทย' 
					value={order.courierTh}
					disable={!isEditMode}
					onChange={(value) => handleInputChange('courierTh', value)}
				/>
				<div className='col-span-4'></div>
			</>
		}

		const renderWeightGroup = () => {
			return <>
				{/* WeightPaid */}
				<div className='col-span-2'>
					<label htmlFor="weightPaid" className="pl-1 flex text-sm font-medium leading-6 text-gray-900 items-center">
						ชำระค่าน้ำหนัก
					</label>
					<select 
						id="weightPaid"
						className={`text-red-900 text-sm w-full py-2 px-1 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 ${!isEditMode ? 'bg-gray-200 cursor-not-allowed' : ''}`}
						value={order.weightPaid ? "true" : "false"} 
						onChange={(e) => handleSelectChange("weightPaid", e.target.value === 'true')}
						disabled={!isEditMode}
					>
						<option value="false">รอชำระ</option>
						<option value="true">ชำระครบแล้ว</option>
					</select>
				</div>
				<Input 
					columnSpan={2} 
					title='น้ำหนัก' 
					value={order.weight}
					disable={!isEditMode}
					onChange={(value) => handleInputChange('weight', value)}
				/>
				<Input 
					columnSpan={2} 
					title='ค่าน้ำหนักเหมา' 
					value={order.weightPrice}
					disable={!isEditMode}
					onChange={(value) => handleInputChange('weightPrice', value)}
				/>
				{/* ตามของ discount: type the FULL fee, the backend takes it off on save */}
				<div className='col-span-4 self-end pb-1'><WeightAssistTag status={weightAssists[order.id]} showHint={isEditMode} /></div>
				<Input 
						columnSpan={2} 
						title='Weight Rate (เรือ)' 
						value={order.weightRate}
						disable={!isEditMode}
						onChange={(value) => handleInputChange('weightRate', value)}
				/>
			</>
		}

		const renderOrderInfo = () => {
			const canChangeOrderStatus = originalOrder?.orderStatus === 'hide';
			return <>
				<Input 
					columnSpan={2} 
					title='Smile Name Input'
					value={order.smileNameInput}
					disable={!isEditMode}
					onChange={(value) => handleInputChange('smileNameInput', value)}
				/>
				
				{/* Order Status - only editable when status is 'hide', allow change hide -> รอเก็บเงิน */}
				<div className='col-span-2'>
					<label htmlFor="orderStatus" className="pl-1 flex text-sm font-medium leading-6 text-gray-900 items-center">
						Order Status
					</label>
					<select 
						id="orderStatus"
						className={`text-red-900 text-sm w-full py-2 px-1 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 ${!(isEditMode && canChangeOrderStatus) ? 'bg-gray-200 cursor-not-allowed' : ''}`}
						value={order.orderStatus ?? ''} 
						onChange={(e) => handleSelectChange("orderStatus", e.target.value)}
						disabled={!(isEditMode && canChangeOrderStatus)}
					>
						<option value="hide">ซ่อน</option>
						<option value="รอเก็บเงิน">รอเก็บเงิน</option>
					</select>
				</div>
				
				{/* Order Date */}
				<div className="col-span-2">
					<label htmlFor="orderDate" className="pl-1 flex text-sm font-medium leading-6 text-gray-900 items-center">
							Order Date
					</label>
					<div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 sm:max-w-md">
						<DatePicker 
							selected={new Date(order.orderDate)}
							onChange={(date) => handleDateChange("orderDate", date)}
							dateFormat="dd-MMM-yyyy"
							className='text-red-900 pl-2 w-full block border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400  sm:text-sm sm:leading-6'
							disabled={!isEditMode}
						/>
					</div>
				</div>
				
				{/* Ship Status */}
				<div className='col-span-2'>
					<label htmlFor="shipStatus" className="pl-1 flex text-sm font-medium leading-6 text-gray-900 items-center">
						สถานะขนส่ง
					</label>
					<select 
						id="shipStatus"
						className={`text-red-900 text-sm w-full py-2 px-1 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 ${!isEditMode ? 'bg-gray-200 cursor-not-allowed' : ''}`}
						value={order.shipStatus} 
						onChange={(e) => handleSelectChange("shipStatus", e.target.value)}
						disabled={!isEditMode}
					>
						<option value='รอส่งในญี่ปุ่น'>รอส่งในญี่ปุ่น</option>
						<option value='ระหว่างขนส่งในญี่ปุ่น'>ระหว่างขนส่งในญี่ปุ่น</option>
						<option value='คลังญี่ปุ่น รอขึ้นเรือ'>คลังญี่ปุ่นรอขึ้นเรือ</option>
						<option value='อยู่บนเรือ'>อยู่บนเรือ</option>
						<option value='คลังไทย'>คลังไทย</option>
						<option value='Cancel by store'>ยกเลิกโดยผู้ขาย</option>
					</select>
				</div>


				{/* Ship Date */}
				<div className="col-span-3">
					<label htmlFor="shipDate" className="pl-1 flex text-sm font-medium leading-6 text-gray-900 items-center">
						รอบเรือ
					</label>
					<select 
						id="shipDate"
						className={`text-red-900 text-sm w-full py-2 px-1 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 ${!isEditMode ? 'bg-gray-200 cursor-not-allowed' : ''}`}
						value={order.shipDateId} 
						onChange={(e) => handleSelectChange("shipDateId", e.target.value)}
						disabled={!isEditMode}
					>
						{/* Add a blank option here */}
						<option value="">{''}</option>
						{shipDates && shipDates.map((shipDate) => (
							<option key={shipDate.id} value={shipDate.id}>
								{(() => {
									const isSpecialAir = shipDate.isAir === true && shipDate.warehouseReceivingAddressId != null;
									const addrNick = shipDate.warehouseReceivingAddress?.addressNickName;
									const modeLabel = shipDate.isAir ? 'เครื่องบิน' : 'เรือ';
									const label = isSpecialAir && addrNick ? ` (${addrNick})` : '';
									return `${formatDate(shipDate.shipDate)} - ${modeLabel}${label}`;
								})()}
							</option>
						))}
					</select>
				</div>


				<Input 
					columnSpan={2} 
					title='จำนวน' 
					value={order.qty}
					disable={!isEditMode}
					onChange={(value) => handleInputChange('qty', value)}
				/>

				{/* Is Credit */}
				<div className="col-span-1">
					<label htmlFor="isCredit" className="pl-1 flex text-sm font-medium leading-6 text-gray-900 items-center">
							Credit
					</label>
					<Checkbox
						checked={order.isCredit}
						onChange={(e) => handleInputChange('isCredit', e.target.checked)}
						inputProps={{ 'aria-label': 'controlled' }}
						disabled={!isEditMode}
					/>
				</div>

				{/* Is SendOnly */}
				<div className="col-span-1">
					<label htmlFor="isSendOnly" className="pl-1 flex text-sm font-medium leading-6 text-gray-900 items-center">
							เป็นฝากส่ง
					</label>
					<Checkbox
						checked={order.isSendOnly}
						onChange={(e) => handleInputChange('isSendOnly', e.target.checked)}
						inputProps={{ 'aria-label': 'controlled' }}
						disabled={!isEditMode}
					/>
				</div>

				<Input 
					columnSpan={2} 
					title='Confirm Purchase URL' 
					value={order.purchaseConfirmUrl}
					disable={!isEditMode}
					onChange={(value) => handleInputChange('purchaseConfirmUrl', value)}
				/>

				{/* Order By */}
				<div className="col-span-2">
					<label htmlFor="orderBy" className="pl-1 flex text-sm font-medium leading-6 text-gray-900 items-center">
						Order By
					</label>
					<select 
						id="orderBy"
						className={`text-red-900 text-sm w-full py-2 px-1 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 ${!isEditMode ? 'bg-gray-200 cursor-not-allowed' : ''}`}
						value={order.orderByOptionId ?? orderByOptions[0]?.id ?? ''} 
						onChange={(e) => handleSelectChange("orderByOptionId", e.target.value ? parseInt(e.target.value, 10) : null)}
						disabled={!isEditMode}
					>
						<option value="">{''}</option>
						{orderByOptions.map((opt) => (
							<option key={opt.id} value={opt.id}>{opt.label}</option>
						))}
					</select>
				</div>

				<div className="col-span-1">
					<label htmlFor="isHandCarry" className="pl-1 flex text-sm font-medium leading-6 text-gray-900 items-center">
							รายการหิ้ว
					</label>
					<Checkbox
						checked={order.handCarry ? true : false}
						onChange={(e) => handleInputChange('isHandCarry', e.target.checked)}
						inputProps={{ 'aria-label': 'controlled' }}
						disabled={!isEditMode}
						sx={{
							'&.Mui-checked': {
								color: 'green',
							},
						}}
					/>
				</div>

				{order.handCarry && (
					<div className="col-span-4">
						<div className="flex items-center justify-between mb-1">
							<label className="pl-1 text-sm font-medium leading-6 text-gray-900">
								ชนิดสินค้าแอร์
							</label>
							{!editingAirTypes && (
								<button
									type="button"
									onClick={startEditingAirTypes}
									className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
								>
									แก้ไข
								</button>
							)}
						</div>
						
						{editingAirTypes ? (
							<div className="border border-indigo-200 rounded-md p-3 bg-indigo-50">
								<div className="space-y-2">
									{airTypeRows.map((row, idx) => {
										const type = airItemTypes.find(t => t.id === row.airItemTypeId);
										const isPerItem = type?.calculationMethod === 'per_item';
										return (
											<div key={idx} className="flex items-end gap-2">
												<div className="flex-1 min-w-0">
													<AirItemTypeTripleDropdown
														list={airItemTypes}
														value={row.airItemTypeId}
														onChange={(id) => updateAirTypeRow(idx, 'airItemTypeId', id)}
														placeholder="เลือกประเภท"
														buttonClassName="w-full flex items-center justify-between rounded border border-gray-300 bg-white px-2 py-1.5 text-left text-sm"
													/>
												</div>
												{isPerItem && (
													<div className="w-16 shrink-0">
														<input
															type="number"
															min="1"
															value={row.qty}
															onChange={(e) => updateAirTypeRow(idx, 'qty', parseInt(e.target.value) || 1)}
															className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
															placeholder="จำนวน"
														/>
													</div>
												)}
												<button
													type="button"
													onClick={() => removeAirTypeRow(idx)}
													className="p-1.5 text-red-500 hover:bg-red-100 rounded shrink-0"
													disabled={airTypeRows.length <= 1}
												>
													<TrashIcon className="h-4 w-4" />
												</button>
											</div>
										);
									})}
								</div>
								<div className="flex justify-between items-center mt-3 pt-2 border-t border-indigo-200">
									<button
										type="button"
										onClick={addAirTypeRow}
										className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800"
									>
										<PlusIcon className="h-4 w-4" />
										เพิ่มประเภท
									</button>
									<div className="flex gap-2">
										<button
											type="button"
											onClick={cancelEditingAirTypes}
											className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100"
										>
											ยกเลิก
										</button>
										<button
											type="button"
											onClick={saveAirTypes}
											disabled={savingAirTypes}
											className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
										>
											{savingAirTypes ? 'กำลังบันทึก...' : 'บันทึก'}
										</button>
									</div>
								</div>
							</div>
						) : (
							<div className="text-sm text-gray-700 py-2 px-2 bg-gray-50 rounded-md border border-gray-200 min-h-[38px]">
								{/* New structure: handCarry.airItems (multiple types) */}
								{order.handCarry?.airItems?.length > 0 ? (
									<div className="space-y-1">
										{order.handCarry.airItems.map((ai, idx) => {
											const type = ai.airItemType;
											const label = type ? [type.category, type.type, type.typeName].filter(Boolean).join(' > ') : '-';
											return (
												<div key={ai.id || idx} className="flex items-center gap-2">
													<span>{label}</span>
													<span className="text-gray-500">x{ai.qty || 1}</span>
												</div>
											);
										})}
									</div>
								) : (
									<span className="text-gray-400">ยังไม่ได้กำหนด</span>
								)}
							</div>
						)}
					</div>
				)}


				{!order.handCarry && (
					<div className="col-span-4"></div>
				)}

				{/* Cancel reason — shown to the customer instead of "Cancel by store".
				    col-start-1 puts it on its own line; the col-span-10 fills the rest of that line. */}
				{order.shipStatus === 'Cancel by store' && (
					<>
						<div className='col-span-2 col-start-1'>
							<label htmlFor="cancelReason" className="pl-1 flex text-sm font-medium leading-6 text-gray-900 items-center">
								เหตุผลยกเลิก
							</label>
							<select
								id="cancelReason"
								className={`text-red-900 text-sm w-full py-2 px-1 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 ${!isEditMode ? 'bg-gray-200 cursor-not-allowed' : ''}`}
								value={order.cancelReason ?? ''}
								onChange={(e) => handleSelectChange("cancelReason", e.target.value)}
								disabled={!isEditMode}
							>
								<option value="">-</option>
								{REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
							</select>
						</div>
						<div className="col-span-10"></div>
					</>
				)}
			</>
		}

		const renderProductInfo = () => {
			return <>
				{/* Product Payment */}
				<div className='col-span-2'>
					<label htmlFor="productPayment" className="pl-1 flex text-sm font-medium leading-6 text-gray-900 items-center">
						ชำระค่าสินค้า
					</label>
					<select 
						id="productPayment"
						className={`text-red-900 text-sm w-full py-2 px-1 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 ${!isEditMode ? 'bg-gray-200 cursor-not-allowed' : ''}`}
						value={order.productPayment} 
						onChange={(e) => handleSelectChange("productPayment", e.target.value)}
						disabled={!isEditMode}
					>
						<option value='คืนเงินแล้ว'>คืนเงินแล้ว</option>
						<option value='รอชำระ'>รอชำระ</option>
						<option value='ชำระครบแล้ว'>ชำระครบแล้ว</option>
						<option value='มัดจำ 30%'>มัดจำ 30%</option>
						<option value='มัดจำ 50%'>มัดจำ 50%</option>
					</select>
				</div>	

				<Input 
					columnSpan={2} 
					title='ค่าสินค้า' 
					value={order.amount}
					disable={!isEditMode}
					onChange={(value) => handleInputChange('amount', value)}
				/>

				<Input 
					columnSpan={2} 
					title='Product URL' 
					value={order.url}
					disable={!isEditMode}
					onChange={(value) => handleInputChange('url', value)}
				/>
				<Input 
					columnSpan={2} 
					title='Image URL' 
					value={order.imageUrl}
					disable={!isEditMode}
					onChange={(value) => handleInputChange('imageUrl', value)}
				/>		

				<Input 
					columnSpan={2} 
					title='Website' 
					value={order.website}
					disable={!isEditMode}
					onChange={(value) => handleInputChange('website', value)}
				/>

				<Input 
					columnSpan={2} 
					title='Product Rate' 
					value={order.productRate}
					disable={!isEditMode}
					onChange={(value) => handleInputChange('productRate', value)}
				/>

				<Input 
					columnSpan={2} 
					title='Seven Fee (เยน)' 
					value={order.sevenFee}
					disable={!isEditMode}
					onChange={(value) => handleInputChange('sevenFee', value)}
				/>

				{/* Is PreOrder */}
				<div className="col-span-1">
					<label htmlFor="isPre" className="pl-1 flex text-sm font-medium leading-6 text-gray-900 items-center">
							PreOrder
					</label>
					<Checkbox
						checked={order.isPre}
						onChange={(e) => handleInputChange('isPre', e.target.checked)}
						inputProps={{ 'aria-label': 'controlled' }}
					/>
				</div>

				{/* PreOrder Date */}
				<div className="col-span-2">
					<label htmlFor="orderDate" className="pl-1 flex text-sm font-medium leading-6 text-gray-900 items-center">
							Pre-Order Date
					</label>
					<div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 sm:max-w-md">
						<DatePicker 
							selected={order.preDate && new Date(order.preDate)}
							onChange={(date) => handleDateChange("preDate", date)}
							dateFormat="dd-MMM-yyyy"
							className='text-red-900 pl-2 w-full block border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400  sm:text-sm sm:leading-6'
							disabled={!isEditMode}
						/>
					</div>
				</div>

				<div className='col-span-1'></div>

			<Input
				columnSpan={2}
				title='Bill Url'
				value={order.billUrl}
				disable={!isEditMode}
				onChange={(value) => handleInputChange('billUrl', value)}
			/>

				{/* Is Down */}
				<div className="col-span-1">
					<label htmlFor="isDown" className="pl-1 flex text-sm font-medium leading-6 text-gray-900 items-center">
							มัดจำสินค้า
					</label>
					<Checkbox
						checked={order.isDown}
						onChange={(e) => handleInputChange('isDown', e.target.checked)}
						inputProps={{ 'aria-label': 'controlled' }}
						disabled={!isEditMode}
					/>
				</div>

				{/* Ship Is Down */}
				<div className="col-span-1">
					<label htmlFor="jpShipIsDown" className="pl-1 flex text-sm font-medium leading-6 text-gray-900 items-center">
							มัดจำค่าส่ง
					</label>
					<Checkbox
						checked={order.jpShipIsDown}
						onChange={(e) => handleInputChange('jpShipIsDown', e.target.checked)}
						inputProps={{ 'aria-label': 'controlled' }}
						disabled={!isEditMode}
					/>
				</div>

				<Input 
					columnSpan={2} 
					title='Down Rate' 
					value={order.downRate}
					disable={!isEditMode}
					onChange={(value) => handleInputChange('downRate', value)}
				/>
				<Input
				columnSpan={2}
				title='Transaction Page URL'
				value={order.transactionPageUrl}
				disable={!isEditMode}
				onChange={(value) => handleInputChange('transactionPageUrl', value)}
				/>

				<Input
					columnSpan={2}
					title='Website Additional Rate'
					value={order.websiteAdditionalRate}
					disable={!isEditMode}
					onChange={(value) => handleInputChange('websiteAdditionalRate', value)}
				/>
			<div className='col-span-8'></div>
			</>
		}

		const renderJapanGroup = () => {
			return <>
				{/* ShipJpPaid */}
				<div className='col-span-2'>
					<label htmlFor="shipJpPaid" className="pl-1 flex text-sm font-medium leading-6 text-gray-900 items-center">
						ชำระค่าส่งญี่ปุ่น
					</label>
					<select 
						id="shipJpPaid"
						className={`text-red-900 text-sm w-full py-2 px-1 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 ${!isEditMode ? 'bg-gray-200 cursor-not-allowed' : ''}`}
						value={order.shipJpPaid} 
						onChange={(e) => handleSelectChange("shipJpPaid", e.target.value)}
						disabled={!isEditMode}
					>
						<option value='รอชำระ'>รอชำระ</option>
						<option value='ชำระครบแล้ว'>ชำระครบแล้ว</option>
						<option value='มัดจำ 30%'>มัดจำ 30%</option>
						<option value='มัดจำ 50%'>มัดจำ 50%</option>
					</select>
				</div>

				<Input 
					columnSpan={2} 
					title='ค่าส่งญี่ปุ่น' 
					value={order.deliveryFeeJp}
					disable={!isEditMode}
					onChange={(value) => handleInputChange('deliveryFeeJp', value)}
				/>
				
			<Input 
				columnSpan={2} 
				title='เลขพัสดุญี่ปุ่น' 
				value={order.trackingJp}
				disable={!isEditMode}
				onChange={(value) => handleInputChange('trackingJp', value)}
			/>

			<Input 
				columnSpan={2} 
				title='ขนส่งในญี่ปุ่น' 
				value={order.courierJp}
				disable={!isEditMode}
				onChange={(value) => handleInputChange('courierJp', value)}
			/>
				
				{/* Is COD */}
				<div className="col-span-2">
					<label htmlFor="isCod" className="pl-1 flex text-sm font-medium leading-6 text-gray-900 items-center">
							COD
					</label>
					<Checkbox
						checked={order.isCod}
						onChange={(e) => handleInputChange('isCod', e.target.checked)}
						inputProps={{ 'aria-label': 'controlled' }}
						disabled={!isEditMode}
					/>
				</div>

				<Input 
					columnSpan={2} 
					title='COD Rate' 
					value={order.codRate}
					disable={!isEditMode}
					onChange={(value) => handleInputChange('codRate', value)}
				/>

			<Input
				columnSpan={2}
				title='Mercari Verify URL'
				value={order.mercariVerifyImageUrl}
				disable={!isEditMode}
				onChange={(value) => handleInputChange('mercariVerifyImageUrl', value)}
			/>

			<Input
				columnSpan={2}
				title='Mercari Verify URL 2'
				value={order.mercariVerifyImageUrl2}
				disable={!isEditMode}
				onChange={(value) => handleInputChange('mercariVerifyImageUrl2', value)}
			/>

			{/* Mercari verify status */}
			<div className='col-span-2'>
				<label htmlFor="mercariVerifyStatus" className="pl-1 flex text-sm font-medium leading-6 text-gray-900 items-center">
					Mercari Verify Status
				</label>
				<select
					id="mercariVerifyStatus"
					className={`text-red-900 text-sm w-full py-2 px-1 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 ${!isEditMode ? 'bg-gray-200 cursor-not-allowed' : ''}`}
					value={order.mercariVerifyStatus || 'pending'}
					onChange={(e) => handleSelectChange('mercariVerifyStatus', e.target.value)}
					disabled={!isEditMode}
				>
					<option value='pending'>รอตรวจ (pending)</option>
					<option value='approved'>อนุมัติ (approved)</option>
					<option value='not_approved'>ไม่อนุมัติ (not_approved)</option>
				</select>
			</div>

			<div className='col-span-6'></div>
			</>
		}
		const renderTransactionFeeGroup = () => {
			return <>
				{/* ShipJpPaid */}
				<div className='col-span-2'>
					<label htmlFor="TransactionFeePaid" className="pl-1 flex text-sm font-medium leading-6 text-gray-900 items-center">
						ชำระค่าค่าบริการสั่งซื้อ
					</label>
					<select 
						id="transactionFeePaid"
						className={`text-red-900 text-sm w-full py-2 px-1 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 ${!isEditMode ? 'bg-gray-200 cursor-not-allowed' : ''}`}
					value={order.transactionFeePaid ? "true" : "false"} 
					onChange={(e) => handleSelectChange("transactionFeePaid", e.target.value === 'true')}
					disabled={!isEditMode}
					>
						<option value="false">รอชำระ</option>
						<option value="true">ชำระครบแล้ว</option>
					</select>
				</div>

				<Input
					columnSpan={2}
					title='ค่าค่าบริการสั่งซื้อ'
					value={order.transactionFee}
					disable={!isEditMode}
					onChange={(value) => handleInputChange('transactionFee', value)}
				/>

				<Input
					columnSpan={2}
					title='Website Transaction Fee'
					value={order.websiteTransactionFee}
					disable={!isEditMode}
					onChange={(value) => handleInputChange('websiteTransactionFee', value)}
				/>

				<div className='col-span-6'></div>

			</>
		}

		const renderExtraJpFeesGroup = () => {
			const fees = Array.isArray(order.extraJpFees) ? order.extraJpFees : [];
			const pendingSuggestions = feeSuggestions.filter(s => !fees.some(f => f.type === s.type));
			if (fees.length === 0 && pendingSuggestions.length === 0) return null;

			return <>
				<div className='col-span-6'>
					<label className="pl-1 flex text-sm font-medium leading-6 text-gray-900 items-center">
						ค่าบริการส่งตรวจ
						{fees.length > 0 && (
							<span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${
								order.extraJpFeesPaid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
							}`}>
								{order.extraJpFeesPaid ? 'ชำระแล้ว' : 'รอชำระ'}
							</span>
						)}
					</label>
					<div className='mt-1 flex flex-wrap gap-2'>
						{fees.map((fee) => (
							<span key={fee.type} className='inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border-2 border-indigo-200 bg-indigo-50 text-indigo-900'>
								{fee.label || fee.type} · {Number(fee.amountThb) > 0 ? `¥${fee.amountJpy} = ฿${fee.amountThb}` : 'ฟรี'}
								<button
									type='button'
									onClick={() => handleRemoveExtraFee(fee)}
									disabled={feeBusy}
									className='text-red-500 hover:text-red-700 font-bold'
									title='ลบค่าบริการนี้'
								>
									✕
								</button>
							</span>
						))}
						{pendingSuggestions.map((s) => (
							<button
								key={s.type}
								type='button'
								onClick={() => handleAddExtraFee(s)}
								disabled={feeBusy}
								className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border-2 ${
									Number(s.amountJpy) > 0
										? 'border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100'
										: 'border-green-400 bg-green-50 text-green-800 hover:bg-green-100'
								}`}
								title='สินค้านี้ส่งตรวจได้ — คลิกเพื่อบันทึกส่งตรวจเข้าออเดอร์'
							>
								{Number(s.amountJpy) > 0
									? `⚠️ ส่งตรวจได้ ค่าบริการ ¥${s.amountJpy} — คลิกเพื่อเพิ่ม`
									: '✅ ส่งตรวจได้ฟรี — คลิกเพื่อบันทึกส่งตรวจ'}
							</button>
						))}
					</div>
				</div>
				<div className='col-span-6'></div>
			</>
		}

		const renderAddressInfo = () => {
			return <>
				<Input 
					columnSpan={3} 
					title='Name' 
					value={order.name}
					disable={!isEditMode}
					onChange={(value) => handleInputChange('name', value)}
				/>
				<Input 
					columnSpan={2} 
					title='Phone' 
					value={order.phone}
					disable={!isEditMode}
					onChange={(value) => handleInputChange('phone', value)}
				/>
				<div className='col-span-5'>
					<Input 
						title='Address' 
						value={order.address}
						disable={!isEditMode}
						onChange={(value) => handleInputChange('address', value)}
					/> 
				</div>
			</>
		}

    return (
        <div>
					<Modal
						isOpen={logIsOpen}
						onRequestClose={handleCloseLog}
						style={customStylesOrderDetail}
						ariaHideApp={false}
					>
						<OrderChangeLog order={order}/>
					</Modal>
        <div className='border-b-1 border-indigo-600'>
            <h3 className='text-2xl font-bold'>Order Detail</h3>
            <div className='grid grid-cols-12 grid-flow-dense gap-4 mt-2 border-b border-gray-900/10 pb-6'>
							{renderOrderInfo()}
							<div className="border-b border-gray-300 my-1 col-span-12"></div>
							{renderProductInfo()}
							<div className="border-b border-gray-300 my-1 col-span-12"></div>
							{renderTransactionFeeGroup()}
							{renderExtraJpFeesGroup()}
							<div className="border-b border-gray-300 my-1 col-span-12"></div>
							{renderJapanGroup()}
							<div className="border-b border-gray-300 my-1 col-span-12"></div>
							{renderWeightGroup()}
							<div className="border-b border-gray-300 my-1 col-span-12"></div>
							{renderThaiGroup()}
							<div className="border-b border-gray-300 my-1 col-span-12"></div>
							{renderAddressInfo()}
            </div>
            <div className="mt-4 text-right">
							<button
								onClick={handleOpenLog}
								className={`mx-1 px-2 py-1 w-40 text-white rounded-md bg-indigo-500 hover:bg-indigo-600`}
							>
								ดูประวัติการแก้ไข
							</button>
							<button
								onClick={toggleEditMode}
								className={`mx-1 px-2 py-1 w-24 text-white rounded-md ${isEditMode ? 'bg-green-500 hover:bg-green-600' : 'bg-blue-500 hover:bg-blue-600'}`}
							>
								{isEditMode ? 'Save' : 'Edit'}
							</button>
            </div>
        </div>
        </div>
    );
};


export default OrderDetail;
